import { HeatmapCellClickPayload } from '@frontend/chart/components';
import { ChartKey } from '@frontend/chart/types';
import { ModalType, useModals } from '@frontend/modal';
import { ReactNode, useCallback, useMemo } from 'react';

import { useInsightDetailMode } from '../insight-detail-mode.context';
import { useInsightDetailFilters } from '../insight-detail/insight-detail-filters.context';
import { getInsightHeatmapCellDrilldown } from './insight-heatmap-cell-drilldown';
import { InsightHeatmapCellSummaryModal } from './insight-heatmap-cell-summary-modal.component';
import { insightHeatmapCellWindow } from './insight-heatmap-cell-window';

const MODAL_ID = 'insight-heatmap-cell-summary-modal';

export interface UseInsightHeatmapCellClickArgs {
    accountId: string;
    chartKey: ChartKey | string;
    title: ReactNode;
}

/** Returns an onCellClick handler only in insight detail mode for supported heatmaps. */
export const useInsightHeatmapCellClick = ({
    accountId,
    chartKey,
    title
}: UseInsightHeatmapCellClickArgs): ((cell: HeatmapCellClickPayload) => void) | undefined => {
    const isDetailMode = useInsightDetailMode();
    const filters = useInsightDetailFilters();
    const { openModal } = useModals();
    const drilldown = useMemo(() => getInsightHeatmapCellDrilldown(chartKey), [chartKey]);
    const enabled = isDetailMode && !!drilldown && !!accountId;

    const onCellClick = useCallback(
        (cell: HeatmapCellClickPayload) => {
            if (!enabled || !drilldown) {
                return;
            }

            const timeWindow = insightHeatmapCellWindow(cell);
            if (!timeWindow) {
                return;
            }

            const entityParams = filters.active ? filters.entityParams : undefined;

            // Defer past the chart click's mousedown/click so DefaultModal's capture-phase
            // outside handler does not dismiss the modal in the same gesture.
            window.setTimeout(() => {
                openModal(
                    MODAL_ID,
                    () => (
                        <InsightHeatmapCellSummaryModal
                            title={title}
                            cell={cell}
                            timeWindow={timeWindow}
                            drilldown={drilldown}
                            accountId={accountId}
                            entityParams={entityParams}
                        />
                    ),
                    {},
                    { type: ModalType.PANE, className: 'max-w-[600px]' }
                );
            }, 0);
        },
        [accountId, drilldown, enabled, filters.active, filters.entityParams, openModal, title]
    );

    return enabled ? onCellClick : undefined;
};
